import {
  Box,
  CheckCircle2,
  CircleDot,
  Clock,
  Layers,
  LoaderCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import { TASK_STATUSES } from "@/constants/workflow";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PRIORITY_STYLE = {
  Low: "text-slate-500 bg-slate-500/10",
  Normal: "text-cyan-700 bg-cyan-500/10",
  High: "text-amber-700 bg-amber-500/15",
  Urgent: "text-red-700 bg-red-500/15",
};

const STATUS_DOT = {
  "To Do": "text-slate-400",
  "In Progress": "text-amber-500",
  Done: "text-emerald-500",
};

const STATUS_ROW = {
  "To Do": "bg-white",
  "In Progress": "bg-violet-50",
  Done: "bg-emerald-50",
};

const fmtDue = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "No due date";

/** One compact task row. */
export function TaskRow({ task, today, canManage, isAssignee, onStatus, onEdit, onDelete }) {
  const overdue = task.status !== "Done" && task.due_date && task.due_date.slice(0, 10) < today;
  const canMove = canManage || isAssignee;

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-3.5 py-2.5 transition-colors ${
        STATUS_ROW[task.status] || "bg-white"
      } ${
        overdue
          ? "border-red-500/50"
          : task.status === "In Progress"
            ? "border-violet-300"
            : task.status === "Done"
              ? "border-emerald-300"
              : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="min-w-[180px] flex-1">
        <p className="truncate text-[13.5px] font-medium text-slate-900">{task.name}</p>
        {task.description ? (
          <p
            data-testid={`task-description-${task.id}`}
            className="mt-0.5 line-clamp-2 text-[11.5px] leading-relaxed text-slate-600"
          >
            {task.description}
          </p>
        ) : null}
        <p className="mono mt-0.5 flex flex-wrap items-center gap-x-2 text-[10.5px] text-slate-500">
          <span className="truncate">
            {task.assignees.length
              ? task.assignees.map((a) => a.name).join(" + ")
              : "Unassigned"}
          </span>
          <span className={`rounded px-1.5 py-0.5 ${PRIORITY_STYLE[task.priority] || ""}`}>
            {task.priority}
          </span>
          <span
            className={`flex items-center gap-1 ${overdue ? "text-red-600" : "text-slate-500"}`}
          >
            <Clock size={10} className={overdue ? "okn-sway" : undefined} />
            {task.start_date ? `${fmtDue(task.start_date)} → ` : overdue ? "Overdue · " : "Due "}
            {fmtDue(task.due_date)}
          </span>
        </p>
        {task.panels?.length || task.phase_name ? (
          <p
            data-testid={`task-links-${task.id}`}
            className="mono mt-1 flex flex-wrap items-center gap-1.5 text-[10px]"
          >
            {task.phase_name ? (
              <span className="flex items-center gap-1 rounded bg-indigo-500/10 px-1.5 py-0.5 text-indigo-700">
                <Layers size={9} /> {task.phase_name}
              </span>
            ) : null}
            {(task.panels || []).slice(0, 4).map((panel) => (
              <span
                key={panel.panel_uid}
                className="flex items-center gap-1 rounded bg-slate-500/10 px-1.5 py-0.5 text-slate-600"
              >
                <Box size={9} /> {panel.panel_name}
              </span>
            ))}
            {(task.panels || []).length > 4 ? (
              <span className="text-slate-500">+{task.panels.length - 4} more</span>
            ) : null}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5">
        {task.status === "Done" ? (
          <CheckCircle2
            key="done"
            size={13}
            className="okn-tick-draw text-emerald-500"
            data-testid={`task-status-icon-${task.id}`}
          />
        ) : task.status === "In Progress" ? (
          <LoaderCircle
            size={13}
            className="okn-spin-slow text-amber-500"
            data-testid={`task-status-icon-${task.id}`}
          />
        ) : (
          <CircleDot
            size={13}
            className={STATUS_DOT[task.status]}
            data-testid={`task-status-icon-${task.id}`}
          />
        )}
        {canMove ? (
          <Select value={task.status} onValueChange={(value) => onStatus(task, value)}>
            <SelectTrigger
              data-testid={`task-status-select-${task.id}`}
              className="mono h-8 w-[132px] border-slate-200 bg-[#EEF2F8] text-[10.5px] uppercase tracking-wider"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              {TASK_STATUSES.map((value) => (
                <SelectItem key={value} value={value} className="text-xs">
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="mono text-[10.5px] uppercase tracking-wider text-slate-600">
            {task.status}
          </span>
        )}

        {canManage ? (
          <>
            <button
              data-testid={`task-edit-${task.id}`}
              onClick={() => onEdit(task)}
              className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <Pencil size={13} />
            </button>
            <button
              data-testid={`task-delete-${task.id}`}
              onClick={() => onDelete(task)}
              className="grid h-7 w-7 place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 size={13} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
